import React, {useState} from "react";
import "./AvatarsView.css";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {Button, Form, Table} from "react-bootstrap";
import {
  Firestore,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
} from "firebase/firestore";
import {uploadImage} from "./cloudinary";

// One document in the Avatars collection. The mobile app uses the document ID as the avatar ID,
// so the document itself stores only `name` and `url`.
type Avatar = {
  id: string;
  name: string;
  url: string;
};

type Props = {
  db: Firestore;
};

const readFileAsDataURL = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const AvatarsView = ({db}: Props) => {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  // A file input cannot be cleared through its value, so a new key mounts an empty one.
  const [fileInputKey, setFileInputKey] = useState(0);

  const {
    data: avatars = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["avatars"],
    queryFn: async () => {
      const querySnapshot = await getDocs(collection(db, "Avatars"));
      return querySnapshot.docs.map(
        avatarDoc => ({id: avatarDoc.id, ...avatarDoc.data()}) as Avatar,
      );
    },
  });

  const addAvatar = async () => {
    if (!file || !name.trim()) {
      window.alert("Pick an image and enter a name.");
      return;
    }

    setSaving(true);
    try {
      const url = await uploadImage(await readFileAsDataURL(file));
      await addDoc(collection(db, "Avatars"), {name: name.trim(), url});
      setName("");
      setFile(null);
      setFileInputKey(key => key + 1);
      await queryClient.invalidateQueries({queryKey: ["avatars"]});
    } catch (e) {
      console.log("Failed to add avatar: ", e);
      window.alert(`Failed to add the avatar: ${e}`);
    } finally {
      setSaving(false);
    }
  };

  const deleteAvatar = async (avatar: Avatar) => {
    if (
      !window.confirm(
        `Delete the avatar "${avatar.name}"? Users who picked it get the built-in avatar.`,
      )
    ) {
      return;
    }

    try {
      await deleteDoc(doc(db, "Avatars", avatar.id));
      await queryClient.invalidateQueries({queryKey: ["avatars"]});
    } catch (e) {
      console.log("Failed to delete avatar: ", e);
      window.alert(`Failed to delete the avatar: ${e}`);
    }
  };

  let list;
  if (isLoading) {
    list = <div>Loading avatars...</div>;
  } else if (isError) {
    list = <div>Failed to load avatars.</div>;
  } else if (avatars.length === 0) {
    list = <div>No avatars yet.</div>;
  } else {
    list = (
      <Table striped>
        <thead>
          <tr>
            <th>Image</th>
            <th>Name</th>
            <th>ID</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {avatars.map(avatar => (
            <tr key={avatar.id}>
              <td>
                <img
                  className="AvatarsView-image"
                  src={avatar.url}
                  alt={avatar.name}
                />
              </td>
              <td>{avatar.name}</td>
              <td>{avatar.id}</td>
              <td>
                <Button
                  variant="outline-danger"
                  onClick={() => deleteAvatar(avatar)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  return (
    <div className="AvatarsView">
      <div className="AvatarsView-content">
        <h2>Avatars</h2>
        <p>
          The mobile app lists its built-in avatar first, then these avatars.
        </p>
        <Form
          className="AvatarsView-add"
          onSubmit={event => {
            event.preventDefault();
            addAvatar();
          }}>
          <Form.Group className="mb-3" controlId="avatar-image">
            <Form.Label>Image</Form.Label>
            <Form.Control
              key={fileInputKey}
              type="file"
              accept="image/*"
              onChange={event =>
                setFile((event.target as HTMLInputElement).files?.[0] ?? null)
              }
            />
            <Form.Text>
              Use a square image of at least 144 × 144 px. The map draws avatars
              48 px wide.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3" controlId="avatar-name">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={event => setName(event.target.value)}
            />
            <Form.Text>
              The Settings tab of the mobile app uses the name as the label.
            </Form.Text>
          </Form.Group>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? "Adding..." : "Add avatar"}
          </Button>
        </Form>
        {list}
      </div>
    </div>
  );
};

export default AvatarsView;
